/**
 * The shell's LV session store — the client half of learner SSO.
 *
 * The auth callback lands the browser on `/?lv_sso=<jwt>`; boot lifts the
 * token into localStorage (`lv.session.v1` — survives reloads, shared by
 * every tab of this deployment) and strips it from the address bar so the
 * JWT never sits in history, referers, or screenshots. Sessions are 24h JWTs
 * with server-side sliding renewal: any API response may carry a fresh token
 * on `X-LV-Refreshed-Token`, which apiFetch adopts transparently. A 401 from
 * the server is authoritative — the stored session is dropped and the app
 * falls back to the login gate (via the session listeners below).
 *
 * This is separate from, and does not touch, each surface's own legacy
 * `#token=` adoption (e.g. src/surfaces/inbox/runtime.ts) — that handoff
 * keeps working until surfaces move to this session.
 */

const SESSION_KEY = 'lv.session.v1'
const REFRESH_HEADER = 'x-lv-refreshed-token'

export interface LvSessionClaims {
  sub: string
  name?: string
  email?: string
  client?: string
  branch?: string
  iat?: number
  exp?: number
}

export interface LvSession {
  token: string
  claims: LvSessionClaims
}

export function decodeClaims(token: string): LvSessionClaims | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as LvSessionClaims
    return typeof claims.sub === 'string' && claims.sub ? claims : null
  } catch {
    return null
  }
}

function isExpired(claims: LvSessionClaims): boolean {
  // Server stays authoritative; the client check exists so an obviously-dead
  // token goes straight to the gate without a doomed API round-trip. 30s of
  // clock-skew slack.
  return typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now() - 30_000
}

/** Lift `?lv_sso=` from the URL into storage and strip it. Call once at boot,
 *  before the first getSession(). Returns true when a fresh token was adopted.
 *  An accompanying `?open=<surfaceId>` (LMS-initiated deep links, e.g. the
 *  first-login credentials redirect) is captured for the shell's boot and
 *  stripped too — the URL is left clean either way. */
export function adoptSessionFromUrl(): boolean {
  try {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('lv_sso')
    const open = url.searchParams.get('open')
    if (open && /^[a-z0-9-]{1,64}$/.test(open)) pendingBootOpenId = open
    if (!token) {
      if (open) {
        url.searchParams.delete('open')
        history.replaceState(null, '', url.pathname + url.search + url.hash)
      }
      return false
    }
    url.searchParams.delete('lv_sso')
    url.searchParams.delete('open')
    history.replaceState(null, '', url.pathname + url.search + url.hash)
    const claims = decodeClaims(token)
    if (!claims || isExpired(claims)) return false
    localStorage.setItem(SESSION_KEY, token)
    notify()
    return true
  } catch {
    return false
  }
}

// A deep-link surface id captured from the boot URL (?open=). One-shot: the
// shell consumes it once when it first renders with a session.
let pendingBootOpenId: string | null = null
export function consumeBootOpenId(): string | null {
  const id = pendingBootOpenId
  pendingBootOpenId = null
  return id
}

export function getSession(): LvSession | null {
  let token: string | null = null
  try {
    token = localStorage.getItem(SESSION_KEY)
  } catch {
    return null // storage blocked (private mode) — behave as signed out
  }
  if (!token) return null
  const claims = decodeClaims(token)
  if (!claims || isExpired(claims)) {
    clearSession()
    return null
  }
  return { token, claims }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch { /* storage blocked */ }
  notify()
}

// ---------------------------------------------------------------------------
// Session listeners — how a 401 deep in the app forces the login gate.
// ---------------------------------------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()

export function onSessionChange(cb: Listener): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function notify(): void {
  for (const cb of listeners) {
    try { cb() } catch { /* a broken listener must not break auth */ }
  }
}

// ---------------------------------------------------------------------------
// apiFetch — fetch with the bearer token, renewal adoption, and 401 handling.
// ---------------------------------------------------------------------------

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const session = getSession()
  const headers = new Headers(init.headers)
  if (session) headers.set('Authorization', `Bearer ${session.token}`)
  const res = await fetch(path, { ...init, headers })

  const refreshed = res.headers.get(REFRESH_HEADER)
  if (refreshed) {
    const claims = decodeClaims(refreshed)
    if (claims && !isExpired(claims)) {
      try { localStorage.setItem(SESSION_KEY, refreshed) } catch { /* storage blocked */ }
    }
  }

  if (res.status === 401 && session) {
    // The server rejected a token we thought was good — it is dead. Drop it;
    // the gate takes over on the next render.
    clearSession()
  }
  return res
}
